import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MarkerType,
  ReactFlowProvider,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { fetchCommunityDetail } from '../services/dashboardService';
import { usePinToBoard } from '../contexts/PinToBoardContext';

// ── Helpers ────────────────────────────────────────────────────────────────

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const nodeWidth = 180;
  const nodeHeight = 60;

  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    return node;
  });

  return { nodes, edges };
};

const getCommunityColor = (id) => {
  if (!id) return '#6366f1';
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 60%, 50%)`;
};

const ENTITY_COLORS = {
  COMPANY: '#3b82f6',
  PERSON: '#22c55e',
  PROPERTY: '#f59e0b',
  LEGAL_CASE: '#ef4444',
};
const entityColor = (type) => ENTITY_COLORS[type?.toUpperCase?.()] || '#9ca3af';

// Convert API nodes/edges to ReactFlow format with improved layout
const buildFlowElements = (apiNodes, apiEdges) => {
  if (!apiNodes || apiNodes.length === 0) return { nodes: [], edges: [] };

  // Calculate degree map for sizing
  const degreeMap = {};
  (apiEdges || []).forEach(e => {
    const src = e.source_node_id || e.source;
    const tgt = e.target_node_id || e.target;
    degreeMap[src] = (degreeMap[src] || 0) + 1;
    degreeMap[tgt] = (degreeMap[tgt] || 0) + 1;
  });

  let nodes = apiNodes.map((n, idx) => {
    const nodeId = String(n.id || n.node_id || idx);
    const degree = degreeMap[nodeId] || 0;
    const label = n.entity_name || n.name || n.label || nodeId;
    const entityType = n.entity_type || n.type || 'UNKNOWN';

    return {
      id: nodeId,
      position: { x: 0, y: 0 },
      data: {
        label,
        entityType,
        degree,
        description: n.description,
        properties: n.properties,
      },
      style: {
        background: entityColor(entityType),
        color: '#fff',
        border: '2px solid rgba(255,255,255,0.3)',
        borderRadius: 10,
        padding: '8px 14px',
        fontSize: 12,
        fontWeight: 600,
        minWidth: 110,
        maxWidth: 180,
        boxShadow: '0 3px 10px rgba(0,0,0,.3)',
        cursor: 'pointer',
        wordBreak: 'break-word',
        textAlign: 'center',
      },
    };
  });

  // Build edges, deduplicating parallel edges
  const edgeMap = new Map();
  (apiEdges || []).forEach(e => {
    const src = String(e.source_node_id || e.source || '');
    const tgt = String(e.target_node_id || e.target || '');
    if (!src || !tgt || src === tgt) return;

    const key = `${src}-${tgt}`;
    const relType = e.relationship_type || e.type || '';
    const context = e.context || e.evidence || '';

    if (edgeMap.has(key)) {
      const ex = edgeMap.get(key);
      if (relType && !ex.label.includes(relType)) {
        ex.label += ` / ${relType}`;
      }
    } else {
      edgeMap.set(key, {
        id: String(e.id || key),
        source: src,
        target: tgt,
        label: relType,
        data: { context, relationshipType: relType },
        type: 'default',
        animated: false,
        style: { stroke: '#94a3b8', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
        labelStyle: {
          fill: '#1e293b',
          fontWeight: 600,
          fontSize: 10,
          background: '#f8fafc',
        },
        labelBgStyle: {
          fill: '#f8fafc',
          fillOpacity: 0.9,
          rx: 3,
          ry: 3,
        },
        labelBgPadding: [4, 6],
      });
    }
  });

  let edges = Array.from(edgeMap.values());
  
  // Apply dagre layout
  const layoutedElements = getLayoutedElements(nodes, edges, 'TB');
  nodes = layoutedElements.nodes;
  edges = layoutedElements.edges;

  return { nodes, edges };
};

// ── GraphCanvas inner ───────────────────────────────────────────────────────

const GraphCanvasInner = ({ rfNodes, rfEdges, onNodeSelect, highlightId }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [hoveredEdge, setHoveredEdge] = useState(null);

  useEffect(() => { setNodes(rfNodes); }, [rfNodes, setNodes]);
  useEffect(() => { setEdges(rfEdges); }, [rfEdges, setEdges]);

  const styledNodes = nodes.map(n => ({
    ...n,
    style: {
      ...n.style,
      outline: n.id === highlightId ? '3px solid #facc15' : 'none',
      outlineOffset: '2px',
      boxShadow: n.id === highlightId ? '0 0 0 4px rgba(250, 204, 21, 0.4), 0 4px 12px rgba(0,0,0,0.3)' : '0 3px 10px rgba(0,0,0,0.2)',
      // Avoid transform as it conflicts with ReactFlow dragging
      zIndex: n.id === highlightId ? 10 : 1,
    },
  }));

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={styledNodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={(_, node) => onNodeSelect(node)}
        onEdgeMouseEnter={(_, edge) => setHoveredEdge(edge)}
        onEdgeMouseLeave={() => setHoveredEdge(null)}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.2}
        maxZoom={2}
        deleteKeyCode={null}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
      >
        <Background color="#cbd5e1" gap={24} size={1} />
        <Controls />
        {hoveredEdge && hoveredEdge.data?.context && (
          <Panel position="bottom-center">
            <div className="cdp-edge-tooltip">
              <strong>Evidence:</strong> {hoveredEdge.data.context}
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
};

// ── Node list item ──────────────────────────────────────────────────────────

const NodeListItem = ({ node, isHighlighted, onClick, onOpenEntity }) => {
  const { pinItem } = usePinToBoard();
  const entityType = node.entity_type || node.type || 'UNKNOWN';
  const nodeId = node.id || node.node_id;
  const entityName = node.entity_name || node.name || node.label || '—';

  return (
    <div
      className={`cdp-node-item ${isHighlighted ? 'highlighted' : ''}`}
      onClick={() => onClick(nodeId)}
    >
      <span
        className="cdp-node-type-chip"
        style={{ background: entityColor(entityType) }}
      >
        {entityType}
      </span>
      <span className="cdp-node-name">{entityName}</span>
      <button
        className="pin-btn"
        title="Pin to Board"
        onClick={(e) => {
          e.stopPropagation();
          pinItem(
            {
              item_type: 'knowledge_node',
              ref_id: String(nodeId),
              title: entityName,
              metadata: { entity_type: entityType },
            },
            e.currentTarget,
          );
        }}
      >
        <i className="fas fa-thumbtack" />
      </button>
      <button
        className="cdp-node-context-btn"
        title="Open Entity Context"
        onClick={e => { e.stopPropagation(); onOpenEntity(node); }}
      >
        <i className="fas fa-expand-alt"></i>
      </button>
    </div>
  );
};

// ── Main CommunityDetailPanel ───────────────────────────────────────────────

const CommunityDetailPanel = ({ community, onBack, onOpenEntity }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rfNodes, setRfNodes] = useState([]);
  const [rfEdges, setRfEdges] = useState([]);
  const [highlightId, setHighlightId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!community?.id) return;
    setLoading(true);
    setError(null);
    setHighlightId(null);

    fetchCommunityDetail(community.id).then(result => {
      if (result.success) {
        const data = result.data;
        setDetail(data);
        const apiNodes = data.nodes || [];
        const apiEdges = data.edges || [];
        const { nodes, edges } = buildFlowElements(apiNodes, apiEdges);
        setRfNodes(nodes);
        setRfEdges(edges);
      } else {
        setError(result.message || 'Failed to load community detail.');
      }
      setLoading(false);
    }).catch(err => {
      setError('Network error loading community.');
      setLoading(false);
    });
  }, [community]);

  const handleNodeSelect = useCallback((rfNode) => {
    const nodeId = rfNode.id;
    setHighlightId(nodeId);
    if (listRef.current) {
      const el = listRef.current.querySelector(`[data-id="${nodeId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  const handleListClick = (id) => {
    setHighlightId(String(id));
  };

  const communityColor = getCommunityColor(community?.id);

  if (loading) {
    return (
      <div className="cdp-loading">
        <div className="cdp-loading-spinner"></div>
        <p>Loading community detail…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cdp-error">
        <i className="fas fa-exclamation-triangle"></i>
        <p>{error}</p>
        <button className="btn-secondary" onClick={onBack}>Go Back</button>
      </div>
    );
  }

  const comm = detail?.community || community;
  const detailNodes = detail?.nodes || [];

  return (
    <div className="cdp-container">
      {/* Back button */}
      <button className="cdp-back-btn" onClick={onBack}>
        <i className="fas fa-arrow-left"></i> Back
      </button>

      <div className="cdp-split">
        {/* ── Left panel ── */}
        <div className="cdp-left-panel">
          <div className="cdp-comm-header" style={{ borderTop: `4px solid ${communityColor}` }}>
            <h2>{comm.title || community.title}</h2>
            <div className="cdp-comm-meta">
              <span className="cdp-rating-pill" style={{ background: communityColor }}>
                Weight: {(((comm.rating ?? community.rating) ?? 0) * 100).toFixed(0)}%
              </span>
              <span className="cdp-node-pill">
                <i className="fas fa-circle-nodes" style={{marginRight: '5px'}}></i>
                {detailNodes.length} entities
              </span>
            </div>
            {(comm.summary || community.summary) && (
              <p className="cdp-comm-summary">{comm.summary || community.summary}</p>
            )}
          </div>

          <div className="cdp-node-list-header">
            <h4>Entities in this community</h4>
          </div>

          <div className="cdp-node-list" ref={listRef}>
            {detailNodes.map(node => {
              const nodeId = String(node.id || node.node_id);
              return (
                <div key={nodeId} data-id={nodeId}>
                  <NodeListItem
                    node={node}
                    isHighlighted={highlightId === nodeId}
                    onClick={handleListClick}
                    onOpenEntity={(n) => {
                      if (onOpenEntity) onOpenEntity(n);
                    }}
                  />
                </div>
              );
            })}
            {detailNodes.length === 0 && (
              <p className="cdp-empty-nodes">No entities found in this community.</p>
            )}
          </div>
        </div>

        {/* ── Right canvas ── */}
        <div className="cdp-graph-canvas">
          {rfNodes.length > 0 ? (
            <ReactFlowProvider>
              <GraphCanvasInner
                rfNodes={rfNodes}
                rfEdges={rfEdges}
                onNodeSelect={handleNodeSelect}
                highlightId={highlightId}
              />
            </ReactFlowProvider>
          ) : (
            <div className="cdp-canvas-empty">
              <i className="fas fa-diagram-project"></i>
              <p>No graph data available for this community.</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default CommunityDetailPanel;
